import type { ArcadeGame } from '../../../game';
import type { GameModeId } from '../../../shared/config';
import { isFeatureEnabled } from '../../../shared/config';

export interface HudDomRefs {
  refScoreValue: { current: HTMLSpanElement | null };
  refObstacleLimitLabel: { current: HTMLSpanElement | null };
  refMissileLock: { current: HTMLDivElement | null };
  refEnemyLock: { current: HTMLDivElement | null };
  refThreatLock: { current: HTMLDivElement | null };
}

/**
 * Per-frame DOM sync for score, block counter, and lock reticles.
 * Extracted from App render loop — no logic changes.
 */
export function syncHudDom(
  arcade: ArcadeGame,
  phase: 'idle' | 'playing' | 'gameover',
  gameMode: GameModeId,
  refs: HudDomRefs,
) {
  if (phase === 'playing') {
    const scoreEl = refs.refScoreValue.current;
    if (scoreEl) {
      scoreEl.textContent = String(arcade.getScoreMs());
    }
    // Live obstacle count vs sandbox slider max
    const blocksEl = refs.refObstacleLimitLabel.current;
    if (blocksEl && isFeatureEnabled('obstacleControls', gameMode)) {
      blocksEl.textContent = `BLOCKS ${arcade.getObstacleCount()}/${arcade.getObstacleLimit()}`;
    }
    // Missile block-lock (green when auto-aim on, silver when off) + enemy lock (orange → red)
    const lockEl = refs.refMissileLock.current;
    if (lockEl) {
      const lock = arcade.getMissileLockScreen(window.innerWidth, window.innerHeight);
      if (lock) {
        lockEl.style.display = 'block';
        lockEl.style.width = `${lock.size}px`;
        lockEl.style.height = `${lock.size}px`;
        lockEl.style.left = `${lock.x}px`;
        lockEl.style.top = `${lock.y}px`;
        lockEl.classList.toggle('arcade-missile-lock--passive', !lock.autoAim);
      } else {
        lockEl.style.display = 'none';
        lockEl.classList.remove('arcade-missile-lock--passive');
      }
    }
    const enemyEl = refs.refEnemyLock.current;
    if (enemyEl) {
      const elock = arcade.getEnemyLockScreen(window.innerWidth, window.innerHeight);
      if (elock) {
        enemyEl.style.display = 'block';
        enemyEl.style.width = `${elock.size}px`;
        enemyEl.style.height = `${elock.size}px`;
        enemyEl.style.left = `${elock.x}px`;
        enemyEl.style.top = `${elock.y}px`;
        enemyEl.classList.toggle('arcade-enemy-lock--hard', elock.hard);
        enemyEl.classList.toggle('arcade-enemy-lock--soft', !elock.hard);
        // Fill tick stacks from the bottom (| | | style progress)
        const lit = Math.round(elock.progress * 8);
        enemyEl.querySelectorAll('.arcade-enemy-lock__ticks').forEach((stack) => {
          stack.querySelectorAll('.arcade-enemy-lock__tick').forEach((tick, i) => {
            // data-tick 0 is top; light from bottom so index 7 fills first
            const fromBottom = 7 - i;
            tick.classList.toggle('arcade-enemy-lock__tick--on', fromBottom < lit);
          });
        });
      } else {
        enemyEl.style.display = 'none';
      }
    }
    // Incoming enemy missile lock (red corners + LOCK + progress)
    const threatEl = refs.refThreatLock.current;
    if (threatEl) {
      const threat = arcade.getThreatLock();
      if (threat) {
        threatEl.style.display = 'block';
        threatEl.classList.add('arcade-threat-lock--active');
        const fill = threatEl.querySelector('[data-threat-fill]') as HTMLElement | null;
        if (fill) {
          fill.style.transform = `scaleX(${threat.progress})`;
        }
      } else {
        threatEl.style.display = 'none';
        threatEl.classList.remove('arcade-threat-lock--active');
      }
    }
  } else {
    const lockEl = refs.refMissileLock.current;
    if (lockEl) {
      lockEl.style.display = 'none';
    }
    const enemyEl = refs.refEnemyLock.current;
    if (enemyEl) {
      enemyEl.style.display = 'none';
    }
    const threatEl = refs.refThreatLock.current;
    if (threatEl) {
      threatEl.style.display = 'none';
      threatEl.classList.remove('arcade-threat-lock--active');
    }
  }
}
